import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { apiService } from '../services/apiService';
import toast from 'react-hot-toast';

// Types
export interface User {
  _id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'user' | 'requisition_user';
  permissions: {
    products: { create: boolean; read: boolean; update: boolean; delete: boolean };
    invoices: { create: boolean; read: boolean; update: boolean; delete: boolean };
    reports: { create: boolean; read: boolean; update: boolean; delete: boolean };
    projects: { create: boolean; read: boolean; update: boolean; delete: boolean };
    users: { create: boolean; read: boolean; update: boolean; delete: boolean };
    settings: { create: boolean; read: boolean; update: boolean; delete: boolean };
  };
  isActive: boolean;
  lastLogin?: Date;
  profileImage?: string;
  phone?: string;
  department?: string;
  designation?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

interface LoginCredentials {
  email?: string;
  username?: string;
  password: string;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'user' | 'requisition_user';
  phone?: string;
  department?: string;
}

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USER'; payload: User };

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  loading: true, // Start with loading true
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

// Context
interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<void>;
  clearError: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
  isAdmin: boolean;
  isManager: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set up token in localStorage
  useEffect(() => {
    if (state.token) {
      localStorage.setItem('token', state.token);
    } else {
      localStorage.removeItem('token');
    }
  }, [state.token]);

  // Persist user to localStorage to survive soft refreshes
  useEffect(() => {
    if (state.user) {
      localStorage.setItem('user', JSON.stringify(state.user));
    } else {
      localStorage.removeItem('user');
    }
  }, [state.user]);

  // Track soft refresh to avoid transient 401 logout
  const lastSoftRefreshAt = React.useRef<number | null>(null);
  useEffect(() => {
    const onSoftRefresh = () => { lastSoftRefreshAt.current = Date.now(); };
    window.addEventListener('app:refresh', onSoftRefresh as any);
    return () => window.removeEventListener('app:refresh', onSoftRefresh as any);
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          dispatch({ type: 'AUTH_START' });
          // Add timeout to prevent hanging
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Auth check timeout')), 2000)
          );
          const responsePromise = apiService.get('/auth/check').catch(err => {
            // If this was triggered by a soft refresh, suppress immediate failure
            if (lastSoftRefreshAt.current && Date.now() - lastSoftRefreshAt.current < 2000) {
              return { data: { user: JSON.parse(localStorage.getItem('user') || 'null') || state.user } } as any;
            }
            throw err;
          });
          
          const response = await Promise.race([responsePromise, timeoutPromise]) as any;
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user: response.data.user, token },
          });
          
          // Preload critical data after successful authentication
          setTimeout(() => {
            apiService.preloadCriticalData().catch(() => {});
          }, 1000);
        } catch (error) {
          // If the failure occurs within 2s of a soft refresh, keep user state
          if (lastSoftRefreshAt.current && Date.now() - lastSoftRefreshAt.current < 2000) {
            dispatch({ type: 'AUTH_SUCCESS', payload: { user: JSON.parse(localStorage.getItem('user') || 'null') || state.user, token } });
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            dispatch({ type: 'AUTH_FAILURE', payload: 'Authentication failed' });
          }
        }
      } else {
        // No token found, user is not authenticated - set loading to false immediately
        dispatch({ type: 'AUTH_FAILURE', payload: 'No token found' });
      }
    };
    
    // Start auth check immediately
    checkAuth();
  }, []);

  // Login function
  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await apiService.post('/auth/login', credentials);
      const { user, token } = response.data;
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token },
      });
      toast.success(`Welcome back, ${user.firstName}!`);
      
      // Preload critical data after successful login
      setTimeout(() => {
        apiService.preloadCriticalData().catch(() => {});
      }, 1000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch({ type: 'LOGOUT' });
  };

  // Register function
  const register = async (data: RegisterData) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await apiService.post('/auth/register', data);
      const { user, token } = response.data;
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token },
      });
      
      toast.success('Registration successful!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Update user function
  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await apiService.put('/users/profile/me', userData);
      dispatch({ type: 'UPDATE_USER', payload: response.data.user });
      toast.success('Profile updated successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Update failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Permission check function
  const hasPermission = (resource: string, action: string): boolean => {
    if (!state.user || !state.user.permissions) return false;
    if (state.user.role === 'admin') return true;
    return state.user.permissions[resource as keyof typeof state.user.permissions]?.[action as keyof typeof state.user.permissions.products] || false;
  };

  // Computed properties
  const isAdmin = state.user?.role === 'admin';
  const isManager = state.user?.role === 'manager' || state.user?.role === 'admin';

  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    login,
    logout,
    register,
    clearError,
    updateUser,
    hasPermission,
    isAdmin,
    isManager,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 